/**
 * push-reminders.ts — Web Push (VAPID) subscribe/unsubscribe + reminder math.
 *
 * Web/PWA only. On a supported browser (serviceWorker + PushManager + Notification)
 * with cloud sync + a signed-in user, enablePush() subscribes via the service worker
 * and persists the subscription to Supabase (push-subscription-store). The server-side
 * scheduler + push Edge Function then deliver notifications even when the app is closed.
 *
 * Unsupported (iOS non-PWA, Capacitor webview, no VAPID key) → degrades to in-tab
 * Notification permission only; never throws.
 *
 * computeUpcomingReminders() stays as the shared spec for the server scheduler's
 * due-window logic (mirrored in supabase/functions/push-scheduler/due-reminders.ts).
 */

import type { CalendarEvent } from "../types";
import { addDaysIso, todayIso } from "./date";
import { expandRecurrence } from "./recurrence";
import { savePushSubscription, deletePushSubscription } from "./push-subscription-store";

const MAX_QUEUE_ITEMS = 200;

type PushItem = { fireAt: number; title: string; body: string };

/** Minimal api shape needed here (avoid importing the full contract). */
type PushApi = { auth?: { user?: { uid?: string } | null } };

/** True when the browser supports Web Push (service worker + PushManager + Notification). */
export function pushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission(): NotificationPermission | "unsupported" {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return window.Notification.permission;
}

/** Decode a url-base64 VAPID public key to the Uint8Array applicationServerKey wants. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = globalThis.atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Request notification permission and, when supported + configured, subscribe this
 * device to Web Push and persist the subscription. Returns the permission result
 * (or "unsupported"). Falls back to plain permission when Web Push isn't available.
 */
export async function enablePush(api: PushApi): Promise<NotificationPermission | "unsupported"> {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";

  const permission = await window.Notification.requestPermission();
  if (permission !== "granted") return permission;

  // No Web Push support or no VAPID key → in-tab notifications only.
  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  const userId = api.auth?.user?.uid;
  if (!pushSupported() || !vapidKey || !userId) return permission;

  try {
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      }));
    await savePushSubscription(subscription.toJSON() as never, userId);
  } catch {
    // Subscription failed (e.g. permission race, push service down) — in-tab still works.
  }
  return permission;
}

/** True when this device currently has an active Web Push subscription. */
export async function isPushSubscribed(): Promise<boolean> {
  if (!pushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    return Boolean(await registration.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/** Unsubscribe this device from Web Push and remove its stored subscription. */
export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    await deletePushSubscription(subscription.endpoint);
    await subscription.unsubscribe();
  } catch {
    // best-effort
  }
}

/**
 * On load / sign-in: if this device already has a Push subscription AND permission is
 * granted, re-persist it for the current user (keeps the DB row fresh, e.g. after the
 * user signs in on a device that previously enabled push). Never prompts; no-op when
 * unsupported / not granted / signed out.
 */
export async function refreshPushSubscription(api: PushApi): Promise<void> {
  const userId = api.auth?.user?.uid;
  if (!pushSupported() || !userId || pushPermission() !== "granted") return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await savePushSubscription(subscription.toJSON() as never, userId);
  } catch {
    // best-effort
  }
}

/** Compute upcoming reminder moments (next 7 days) as absolute epoch ms (local tz). */
export function computeUpcomingReminders(events: CalendarEvent[], nowMs = Date.now()): PushItem[] {
  const start = todayIso();
  const end = addDaysIso(7);
  const items: PushItem[] = [];

  for (const event of events) {
    if (!event.reminders?.length || !/^\d{2}:\d{2}$/.test(event.time)) continue;
    for (const date of expandRecurrence(event, start, end)) {
      const base = new Date(`${date}T${event.time}:00`).getTime(); // local-time wall clock
      if (Number.isNaN(base)) continue;
      for (const offset of event.reminders) {
        const fireAt = base - offset * 60_000;
        if (fireAt <= nowMs) continue; // only future moments
        const label = offset === 0 ? `Đúng giờ — ${event.time}` : `Còn ${offset} phút — ${event.time}`;
        items.push({ fireAt, title: `🔔 ${event.title}`, body: label });
      }
    }
  }
  return items.sort((a, b) => a.fireAt - b.fireAt).slice(0, MAX_QUEUE_ITEMS);
}
